import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSavedItems } from '../contexts/SavedItemsContext';
import ScheduleVisitModal from '../components/ScheduleVisitModal';
import api from '../services/api';
import './SavedPage.css';

const placeholderImg = 'data:image/svg+xml,' + encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="400" height="220" fill="#f1f5f9">' +
  '<rect width="400" height="220"/>' +
  '<g transform="translate(175, 75)" fill="none" stroke="#94a3b8" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">' +
    '<path d="M3 21h48M5 21V9l20-7 20 7v12"/>' +
    '<rect x="18" y="13" width="14" height="8" rx="1"/>' +
    '<line x1="25" y1="13" x2="25" y2="21"/>' +
    '<line x1="18" y1="17" x2="32" y2="17"/>' +
    '<path d="M10 21v-8h6v8M34 21v-8h6v8"/>' +
  '</g>' +
  '</svg>'
);

export default function SavedPage() {
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [visitModalOpen, setVisitModalOpen] = useState({});
  const { toggleSave } = useSavedItems();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/api/saved-items?item_type=compound');
        if (!cancelled) setSaved(data.items || []);
      } catch (err) {
        console.error('Failed to load saved items', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const toggle = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleUnsave = async (item) => {
    await toggleSave(item.item_key, item.item_data);
    setSaved(prev => prev.filter(s => s.item_key !== item.item_key));
    setSelected(prev => prev.filter(id => id !== item.item_key));
  };

  const compareUrl = selected.length >= 2
    ? `/compare?ids=${selected.join(',')}`
    : '/compare';

  if (loading) {
    return (
      <div className="saved-page page-content">
        <h1>Saved</h1>
        <p className="page-subtitle">Loading your saved compounds…</p>
      </div>
    );
  }

  return (
    <div className="saved-page page-content">
      <h1>Saved</h1>
      <p className="page-subtitle">Your saved compounds. Select two or more to compare.</p>
      <div className="saved-actions">
        <Link
          to={compareUrl}
          state={{ freshContext: true }}
          className={`compare-btn ${selected.length >= 2 ? '' : 'disabled'}`}
        >
          Compare selected ({selected.length})
        </Link>
      </div>
      {saved.length === 0 ? (
        <div className="saved-empty">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5">
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
          </svg>
          <p>No saved compounds yet.</p>
          <p className="saved-empty-hint">Search for properties in the chat and click Save to bookmark them here.</p>
        </div>
      ) : (
        <div className="saved-grid">
          {saved.map((item) => {
            const c = item.item_data || {};
            const key = item.item_key || item.id;
            const name = c.project_name || c.name || c.title || 'Compound';
            const imgSrc = (c.images && c.images[0]) || null;
            const imgUrl = imgSrc && (typeof imgSrc === 'string' ? imgSrc : imgSrc.url);
            const facilities = c.facilities || c.amenities || '';
            const facilitiesList = facilities
              ? facilities.split(/[;,]/).map(f => f.trim()).filter(Boolean).slice(0, 4)
              : [];

            return (
              <div key={item.id} className={`saved-card${selected.includes(key) ? ' selected' : ''}`}>
                <label className="saved-checkbox">
                  <input
                    type="checkbox"
                    checked={selected.includes(key)}
                    onChange={() => toggle(key)}
                  />
                  <span className="saved-checkbox-custom" />
                </label>
                <div className="saved-card-image">
                  <img src={imgUrl || placeholderImg} alt={name} />
                </div>
                <div className="saved-card-body">
                  <h3>{name}</h3>
                  {c.location && (
                    <p className="saved-meta saved-meta-location">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                      {c.location}
                    </p>
                  )}
                  {c.average_area && (
                    <p className="saved-meta">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/></svg>
                      {c.average_area}
                    </p>
                  )}
                  {c.payment_plans && (
                    <p className="saved-meta">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
                      {c.payment_plans}
                    </p>
                  )}
                  {facilitiesList.length > 0 && (
                    <div className="saved-tags">
                      {facilitiesList.map((f, i) => <span key={i} className="saved-tag">{f}</span>)}
                      {facilities.split(/[;,]/).filter(Boolean).length > 4 && (
                        <span className="saved-tag saved-tag-more">+{facilities.split(/[;,]/).filter(Boolean).length - 4}</span>
                      )}
                    </div>
                  )}
                  <div className="saved-card-actions">
                    <Link to={`/compound/${key}`} state={{ freshContext: true }} className="saved-link">
                      View & chat
                    </Link>
                    <button
                      type="button"
                      className="saved-schedule-btn"
                      onClick={() => setVisitModalOpen(prev => ({ ...prev, [key]: true }))}
                    >
                      Schedule visit
                    </button>
                    <button
                      type="button"
                      className="saved-unsave-btn"
                      onClick={() => handleUnsave(item)}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="#E8A020" stroke="#E8A020" strokeWidth="2">
                        <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                      </svg>
                      Unsave
                    </button>
                  </div>
                </div>
                  <ScheduleVisitModal
                    isOpen={!!visitModalOpen[key]}
                    onClose={() => setVisitModalOpen(prev => ({ ...prev, [key]: false }))}
                    compound={{ compound_id: key, project_name: name, location: c.location }}
                    onSuccess={() => setVisitModalOpen(prev => ({ ...prev, [key]: false }))}
                  />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
